import { motion } from "motion/react";
import { Brain, Leaf, HeartHandshake, CheckCircle } from "lucide-react";
import { missionCopy } from "../lib/landingData";
import { HolographicCard } from "../components/HolographicCard";

export function MissionSection() {
  return (
    <section id="mission" className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Soft glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[600px] w-[800px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-6">
              {missionCopy.badge}
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-foreground mb-6">
              {missionCopy.headline}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {missionCopy.subheadline}
            </p>
            <ul className="space-y-4">
              {missionCopy.points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-muted-foreground">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <HolographicCard className="h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-primary/20">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Focus-first design</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Context switching burns energy. By unifying tasks, messages, calendar, and AI
                into one calm surface, FlowState reduces the mental tax of modern work.
              </p>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Leaf className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">{missionCopy.secondaryHeadline}</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {missionCopy.secondarySubheadline}
              </p>
              <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
                <HeartHandshake className="h-4 w-4 text-primary" />
                <span>Built for humans, by humans who need it too.</span>
              </div>
            </HolographicCard>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
